import { ArrowLeft, Building2, Loader2, Plus, Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { adminClient, type AdminFirstOwner, type AdminWorkOSOrganization } from '@/api/adminClient';
import { StatusPill } from '@/components/runs/StatusPill';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { orgBasePath } from '@/lib/orgNav';

type Step = { kind: 'find' } | { kind: 'turn-on'; organization: AdminWorkOSOrganization } | { kind: 'create' };

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function ownerToast(name: string, owner: AdminFirstOwner | undefined): string {
  if (!owner) return `Studio is on for ${name}. Add its first owner from People`;
  return owner.kind === 'seat'
    ? `Studio is on for ${name}. ${owner.email} is its owner`
    : `Studio is on for ${name}. ${owner.email} is invited as its owner`;
}

function OrganizationResult({
  organization,
  onTurnOn,
  onOpen,
}: {
  organization: AdminWorkOSOrganization;
  onTurnOn: () => void;
  onOpen: () => void;
}) {
  return (
    <li className="flex items-center gap-3 rounded-lg px-3 py-2 hover:bg-muted/60">
      <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-foreground">{organization.name}</p>
        <p className="truncate text-xs text-muted-foreground">
          {organization.domains.length > 0 ? organization.domains.join(', ') : organization.organizationId}
        </p>
      </div>
      {organization.studio ? (
        <>
          <StatusPill tone="success">In Studio</StatusPill>
          <Button type="button" size="sm" variant="ghost" onClick={onOpen}>
            Open
          </Button>
        </>
      ) : (
        <Button type="button" size="sm" variant="outline" onClick={onTurnOn}>
          Turn on Studio
        </Button>
      )}
    </li>
  );
}

/**
 * Studio Admin's way to add an organization: find it among every organization
 * in the shared WorkOS environment and turn Studio on, or create a new one.
 * Either way the first owner gets their seat now, or an invitation carrying it.
 */
export function AddOrganizationDialog({ open, onClose, onAdded }: { open: boolean; onClose: () => void; onAdded: () => void }) {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>({ kind: 'find' });
  const [query, setQuery] = useState('');
  const debounced = useDebouncedValue(query.trim(), 250);
  const [results, setResults] = useState<{ items: AdminWorkOSOrganization[]; total: number } | undefined>();
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | undefined>();
  const [name, setName] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [fieldError, setFieldError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStep({ kind: 'find' });
    setQuery('');
    setResults(undefined);
    setOwnerEmail('');
    setFieldError(undefined);
  }, [open]);

  useEffect(() => {
    if (!open || step.kind !== 'find') return undefined;
    let cancelled = false;
    setSearching(true);
    setSearchError(undefined);
    adminClient
      .searchWorkOSOrganizations(debounced)
      .then((next) => !cancelled && setResults(next))
      .catch((error: unknown) => !cancelled && setSearchError(error instanceof Error ? error.message : 'Search failed'))
      .finally(() => !cancelled && setSearching(false));
    return () => {
      cancelled = true;
    };
  }, [open, step.kind, debounced]);

  const openOrganization = (organizationId: string) => {
    onClose();
    navigate(orgBasePath(organizationId));
  };

  const submit = async () => {
    const email = ownerEmail.trim();
    if (email && !EMAIL_RE.test(email)) {
      setFieldError('Enter a valid email, or leave it empty to add an owner later');
      return;
    }
    if (step.kind === 'create' && !name.trim()) {
      setFieldError('Enter a name for the organization');
      return;
    }
    setBusy(true);
    try {
      const result =
        step.kind === 'turn-on'
          ? await adminClient.turnOnStudio(step.organization.workosId, email ? { ownerEmail: email } : {})
          : await adminClient.createStudioOrganization({ name: name.trim(), ...(email ? { ownerEmail: email } : {}) });
      toast.success(ownerToast(result.organization.name, result.owner));
      onAdded();
      openOrganization(result.organization.organizationId);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not add the organization');
    } finally {
      setBusy(false);
    }
  };

  if (step.kind === 'find') {
    const trimmed = query.trim();
    return (
      <Dialog
        open={open}
        onClose={onClose}
        title="Add organization"
        description="Find the customer's organization in WorkOS and turn Studio on. Create one only when they have none yet."
        className="max-w-xl"
      >
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <Input
            aria-label="Search WorkOS organizations"
            placeholder="Search by name, domain or id"
            className="pl-9"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            autoComplete="off"
            autoFocus
          />
        </div>
        <div className="mt-3 min-h-[12rem]">
          <div className="max-h-80 overflow-y-auto">
            {searchError ? (
              <p className="px-3 py-6 text-sm text-destructive">{searchError}</p>
            ) : !results ? (
              <p className="flex items-center gap-2 px-3 py-6 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                Searching WorkOS
              </p>
            ) : results.items.length === 0 ? (
              <p className="px-3 py-6 text-sm text-muted-foreground">
                {trimmed ? `No organization in WorkOS matches “${trimmed}”.` : 'No organizations in WorkOS yet.'}
              </p>
            ) : (
              <ul className={searching ? 'opacity-60' : undefined} aria-busy={searching}>
                {results.items.map((organization) => (
                  <OrganizationResult
                    key={organization.workosId}
                    organization={organization}
                    onOpen={() => openOrganization(organization.organizationId)}
                    onTurnOn={() => {
                      setFieldError(undefined);
                      setStep({ kind: 'turn-on', organization });
                    }}
                  />
                ))}
              </ul>
            )}
          </div>
          {results && results.total > results.items.length ? (
            <p className="px-3 pt-2 text-xs text-muted-foreground">
              Showing {results.items.length} of {results.total}. Type more to narrow it down.
            </p>
          ) : null}
        </div>
        <div className="mt-4 flex items-center justify-between gap-3 border-t border-border pt-4">
          <p className="text-sm text-muted-foreground">Not in WorkOS yet?</p>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setName(trimmed);
              setFieldError(undefined);
              setStep({ kind: 'create' });
            }}
          >
            <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
            Create organization
          </Button>
        </div>
      </Dialog>
    );
  }

  const turningOn = step.kind === 'turn-on';
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={turningOn ? `Turn on Studio for ${step.organization.name}` : 'Create organization'}
      description={
        turningOn
          ? 'Its people, SSO and other products stay as they are. The first owner then adds their own team from Studio.'
          : 'A new WorkOS organization, already set up for Studio. Set up SSO and domains for it in WorkOS later if the customer needs them.'
      }
      footer={
        <>
          <Button type="button" variant="outline" onClick={() => setStep({ kind: 'find' })} disabled={busy}>
            <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" />
            Back
          </Button>
          <Button type="submit" form="add-organization-form" disabled={busy}>
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> : null}
            {turningOn ? 'Turn on Studio' : 'Create organization'}
          </Button>
        </>
      }
    >
      <form
        id="add-organization-form"
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        {turningOn ? null : (
          <div>
            <Label htmlFor="add-org-name">Name</Label>
            <Input
              id="add-org-name"
              className="mt-2"
              placeholder="Travis County 911"
              autoFocus
              value={name}
              onChange={(event) => {
                setName(event.target.value);
                setFieldError(undefined);
              }}
            />
          </div>
        )}
        <div>
          <Label htmlFor="add-org-owner">First owner</Label>
          <Input
            id="add-org-owner"
            className="mt-2"
            type="text"
            inputMode="email"
            autoComplete="off"
            placeholder="owner@customer.example"
            autoFocus={turningOn}
            value={ownerEmail}
            onChange={(event) => {
              setOwnerEmail(event.target.value);
              setFieldError(undefined);
            }}
          />
          <p className="mt-1.5 text-xs text-muted-foreground">
            Someone already in WorkOS gets the owner seat now. Anyone else gets an invitation. Leave it empty to add one later.
          </p>
          {fieldError ? <p className="mt-1.5 text-xs text-destructive">{fieldError}</p> : null}
        </div>
      </form>
    </Dialog>
  );
}
